/**
 * Tuning for the screen-share sender's video encoding.
 *
 * A screen is captured at its full native resolution — on a Retina/4K display
 * that can be ~3456×2234 (~7.7 MP). Encoding and delivering frames that large is
 * the root of the "black tile for several seconds" symptom: the stream is too
 * heavy for low-power and mobile viewers (iOS Safari software-decodes video) and
 * pushes the system towards unreliable rendering. Capping the resolution (and
 * frame rate) to sane values keeps screen text legible while letting every
 * viewer — desktop and mobile — render it promptly. The codec is deliberately
 * left at the browser default (VP8): forcing H.264 made Kurento transcode
 * H.264→VP8 for viewers, and that transcode path does not emit a keyframe on
 * subscribe, so late-joining viewers saw a long black tile.
 */

// Modest bump over the default screen-share target to keep text sharp.
export const SCREEN_SHARE_MAX_BITRATE = 3_000_000;

// Cap the encoded screen-share long edge. A full Retina/4K capture is far heavier
// than viewers (mobile especially) can render promptly; 1920 keeps text crisp
// (full-HD) while keeping the stream light enough to decode reliably.
export const SCREEN_SHARE_MAX_DIMENSION = 1920;

// Screen content barely changes frame-to-frame; a low frame rate is plenty and
// further reduces per-frame load. Applied to the single shared encoding, so it
// affects every viewer — which is fine for a screen share.
export const SCREEN_SHARE_MAX_FRAMERATE = 15;

/**
 * Compute the `scaleResolutionDownBy` factor that brings a capture's long edge
 * down to `maxDimension`. Returns 1 (no scaling) when the capture already fits or
 * its dimensions aren't known yet. WebRTC requires a factor >= 1.
 */
const computeScreenShareScaleDown = (
    width: number | undefined,
    height: number | undefined,
    maxDimension: number,
): number => {
    const longEdge = Math.max(width ?? 0, height ?? 0);
    if (!longEdge || longEdge <= maxDimension) {
        return 1;
    }
    return longEdge / maxDimension;
};

/**
 * Best-effort: tune the screen-share sender's single encoding.
 *
 * - Caps resolution to `SCREEN_SHARE_MAX_DIMENSION` on the long edge. A full
 *   Retina/4K capture is too heavy to render promptly on viewers (mobile
 *   especially); downscaling to full-HD keeps text legible while making the
 *   stream decodable everywhere.
 * - Caps the frame rate to `SCREEN_SHARE_MAX_FRAMERATE` (screen content is mostly
 *   static; this trims per-frame load with no perceptible loss).
 * - Raises the bitrate ceiling so text stays sharp.
 *
 * Deliberately leaves `degradationPreference` (and `contentHint`) at their
 * defaults: a `maintain-resolution` / `'detail'` bias makes the encoder hold
 * resolution by collapsing the frame rate, which on a static screen stops new
 * frames almost entirely — late-joining viewers then see black until the screen
 * changes. The default balance keeps frames flowing.
 */
export const applyScreenShareEncoding = (
    peerConnection: RTCPeerConnection | null,
    maxBitrate: number = SCREEN_SHARE_MAX_BITRATE,
): void => {
    try {
        const sender = peerConnection
            ?.getSenders?.()
            .find((candidate) => candidate.track?.kind === 'video');
        if (!sender?.getParameters) {
            return;
        }
        const parameters = sender.getParameters();
        if (!parameters.encodings?.length) {
            parameters.encodings = [{}];
        }
        const settings = sender.track?.getSettings?.();
        parameters.encodings[0].maxBitrate = maxBitrate;
        parameters.encodings[0].maxFramerate = SCREEN_SHARE_MAX_FRAMERATE;
        parameters.encodings[0].scaleResolutionDownBy =
            computeScreenShareScaleDown(
                settings?.width,
                settings?.height,
                SCREEN_SHARE_MAX_DIMENSION,
            );
        sender.setParameters(parameters).catch(() => {});
    } catch (_e) {
        // Best-effort tuning; ignore unsupported-parameter errors.
    }
};

const VP8_RTPMAP_PATTERN = /^a=rtpmap:(\d+)\s+VP8\/90000/i;

/**
 * Make a screen-share receiver prefer VP8 by reordering the `m=video` payload
 * types in its SDP offer so VP8 comes first.
 *
 * iOS Safari negotiates H.264 by default, which forces the SFU (Kurento) to
 * transcode the forwarded VP8 source to H.264 for that viewer. The transcode
 * output doesn't emit a keyframe on subscribe, so a viewer joining/reloading
 * mid-share has no keyframe to start decoding and sees a black tile until the
 * next (rare) natural keyframe. Offering VP8 first lets the SFU answer VP8 and
 * forward the stream verbatim (no transcode) — the path that already renders
 * promptly for desktop viewers.
 *
 * SDP munging (rather than `setCodecPreferences`) is used because kurento-utils
 * adds the receive-only transceiver inside `generateOffer`, after which the
 * offer is created synchronously — leaving no point at which a transceiver
 * exists to set preferences on before the offer is built.
 *
 * Reorders only the payload-type *preference*; it never removes a codec, so a
 * client that only advertises H.264 keeps it (the offer has no VP8 line → no-op)
 * and a desktop (already VP8-first) is unaffected. Returns the SDP unchanged on
 * any non-string input or when no `m=video` / VP8 line is present.
 *
 * Reorders the first `m=video` section only, which is all these receive-only,
 * single-video legs ever carry.
 */
export const preferVp8InSdp = (sdp: string): string => {
    if (!sdp || typeof sdp !== 'string') {
        return sdp;
    }
    const lines = sdp.split(/\r\n|\n/);
    const videoLineIndex = lines.findIndex((line) =>
        line.startsWith('m=video'),
    );
    if (videoLineIndex === -1) {
        return sdp;
    }
    const vp8PayloadTypes = lines.reduce<string[]>((payloadTypes, line) => {
        const match = VP8_RTPMAP_PATTERN.exec(line);
        if (match) {
            payloadTypes.push(match[1]);
        }
        return payloadTypes;
    }, []);
    if (!vp8PayloadTypes.length) {
        return sdp;
    }
    const vp8PayloadTypeSet = new Set(vp8PayloadTypes);
    const parts = lines[videoLineIndex].split(' ');
    const header = parts.slice(0, 3); // "m=video", port, protocol
    const payloadTypes = parts.slice(3);
    /*
     * Partition the m=video line's own payload types so VP8 comes first. Walking
     * payloadTypes (rather than emitting from vp8PayloadTypes) guarantees each
     * type appears exactly once, so a VP8 type shared across multiple m=video
     * sections can never be duplicated onto this line.
     */
    const reordered = [
        ...payloadTypes.filter((pt) => vp8PayloadTypeSet.has(pt)),
        ...payloadTypes.filter((pt) => !vp8PayloadTypeSet.has(pt)),
    ];
    lines[videoLineIndex] = [...header, ...reordered].join(' ');
    return lines.join('\r\n');
};
